import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';

import ButtonPrimary from '@design-system/ButtonPrimary';
import ButtonOutlined from '@design-system/ButtonOutlined';
import { useTranslation, Trans } from 'react-i18next';
import useStyles from 'components/MenuMakeDeleteAll/styles';
import { useState, useEffect } from 'react';
import { useDeleteMenu, useGetMenuInfo } from 'components/MenuMakeDeleteAll/hooks';
import { useNotification } from '@design-system/Notification/useNotification';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

interface Props {
  open: boolean;
  onCancel: () => void;
  menuUuid: string;
  onSuccess: () => void;
}
interface MenuInfo {
  categories: number;
  products: number;
  modifierGroups: number;
  options: number;
}
interface MenuInfoResponse {
  data: MenuInfo;
}
export function ModalDeleteAll({ open, onCancel, menuUuid, onSuccess }: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isReadyToDelete, setIsReadyToDelete] = useState(false);
  const { mutate: getMenuInfo, isLoading: isGettingInfo } = useGetMenuInfo();
  const { mutate: deleteMenu, isLoading: isDeleting } = useDeleteMenu();
  const { setNotification } = useNotification();
  const [menuInfo, setMenuInfo] = useState<MenuInfo>({
    categories: 0,
    products: 0,
    modifierGroups: 0,
    options: 0,
  });
  useEffect(() => {
    if (menuUuid) {
      handleGetMenuInfo();
    }
  }, [menuUuid]);

  const handleDeleteMenu = () => {
    deleteMenu(menuUuid, {
      onSuccess: () => {
        setNotification(t('menuMaker:deleteAllMenu.deleteSuccess'), {
          variant: 'success',
        });
        onSuccess();
      },
      onError: () => {
        setNotification(t('menuMaker:deleteAllMenu.deleteError'), {
          variant: 'error',
        });
        onCancel();
      },
    });
  };
  const handleGetMenuInfo = () => {
    getMenuInfo(menuUuid, {
      onSuccess: (data: MenuInfoResponse) => {
        setMenuInfo(data.data);
        setIsReadyToDelete(true);
      },
      onError: () => {
        setNotification(t('menuMaker:deleteAllMenu.getInfoError'), {
          variant: 'error',
        });
      },
    });
  };

  return (
    <Dialog classes={{ paper: classes.paper }} data-testid="ModalDeleteAll" fullWidth maxWidth="md" open={open}>
      <DialogTitle align="center" className={classes.dialogTitle} id="alert-dialog-title">
        <IconWarning />
        <Trans i18nKey="menuMaker:deleteAllMenu.title" />
      </DialogTitle>

      {isDeleting && (
        <Box
          alignItems="center"
          bgcolor="rgba(255, 255, 255, 0.5)"
          bottom={0}
          display="flex"
          justifyContent="center"
          left={0}
          marginTop={2}
          position="absolute"
          right={0}
          top={0}
          zIndex={1000}
        />
      )}
      <DialogContent>
        <Box display="flex" flexDirection="column" justifyContent="center">
          <DialogContentText align="center" className={classes.dialogContentText}>
            {t('menuMaker:deleteAllMenu.description')}
          </DialogContentText>
          <DialogContentText
            align="center"
            className={classes.dialogContentText}
            style={{
              color: '#DF130E',
              fontWeight: 500,
              marginTop: 16,
            }}
          >
            <Trans i18nKey="menuMaker:deleteAllMenu.warning" />
          </DialogContentText>
        </Box>
        {isGettingInfo ? (
          <Box alignItems="center" display="flex" justifyContent="center" mt={4}>
            <CircularProgress color="secondary" size={36} />
          </Box>
        ) : (
          <Box display="flex" gap={1} justifyContent="space-evenly" mt={4} textAlign="center">
            <Box>
              <Typography fontWeight={500} variant="body1">
                {t('menuMaker:deleteAllMenu.categories')}
              </Typography>
              <Typography variant="body1">{menuInfo.categories}</Typography>
            </Box>
            <Box>
              <Typography fontWeight={500} variant="body1">
                {t('menuMaker:deleteAllMenu.products')}
              </Typography>
              <Typography variant="body1">{menuInfo.products}</Typography>
            </Box>
            <Box>
              <Typography fontWeight={500} variant="body1">
                {t('menuMaker:deleteAllMenu.modifierGroups')}
              </Typography>
              <Typography variant="body1">{menuInfo.modifierGroups}</Typography>
            </Box>
            <Box>
              <Typography fontWeight={500} variant="body1">
                {t('menuMaker:deleteAllMenu.options')}
              </Typography>
              <Typography variant="body1">{menuInfo.options}</Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <ButtonOutlined
          className={classes.actionButton}
          data-testid="btnModalDeleteAllCancel"
          disabled={isDeleting}
          onClick={onCancel}
        >
          {t('menuMaker:deleteAllMenu.buttonCancel')}
        </ButtonOutlined>

        <ButtonPrimary
          className={classes.actionButton}
          data-testid="btnModalDeleteAllAccept"
          disabled={!isReadyToDelete}
          loading={isDeleting}
          onClick={handleDeleteMenu}
          style={{
            ...(isReadyToDelete && !isDeleting && { backgroundColor: '#DF130E' }),
          }}
        >
          {t('menuMaker:deleteAllMenu.buttonAccept')}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
}

const IconWarning = () => {
  return (
    <svg fill="none" height="56" viewBox="0 0 56 56" width="56" xmlns="http://www.w3.org/2000/svg">
      <circle cx="28" cy="28" fill="#DF130E" r="28" />
      <path clipRule="evenodd" d="M26.543 32.6673V16.334H29.4596V32.6673H26.543Z" fill="white" fillRule="evenodd" />
      <path
        clipRule="evenodd"
        d="M26.543 39.6678L26.543 36.7871L29.4596 36.7871L29.4596 39.6678L26.543 39.6678Z"
        fill="white"
        fillRule="evenodd"
      />
    </svg>
  );
};
